// Shared UI-level types for the PDF editor route and its components:
// the tool palette, the in-progress content-text edit, drag phases and
// per-tool styling forwarded to pages for live previews.
import type {
  DashStyle,
  HeadStyle,
  MarkupKind,
  Quad,
  ShapeKind,
  TextAlign,
} from "./protocol.ts";

// ─────────────────────────── constants ───────────────────────────

// "select" is the pointer — the one persistent mode, and the only tool
// that both selects and edits. A press resolves whatever is under it
// (annotation, form field, content text line, image, vector fill) and
// selects it; pressing the same thing again opens its editor. It used
// to share that job with a separate "edittext" tool, which could edit
// document content but made every annotation inert while it was on.
// They are one tool now: selecting IS how you start editing.
// "stamp" is a transient placement mode entered after choosing a
// signature or image — the next page click drops it, then back to select.
// "whiteout" is a styled rect shape; "redact" drags over text (glyph
// quads) or an area (rect fallback) and is applied for real at export.
// "hand" is the only tool that creates nothing: pages ignore its
// pointer events so the drag falls through to the scroller, which pans.
export type Tool =
  | "select"
  | "hand"
  | "text"
  | MarkupKind
  | "ink"
  | ShapeKind
  | "note"
  | "stamp"
  | "whiteout"
  | "bgblock"
  | "redact"
  | "link"
  | "crop"
  | "field"
  | "measure";

/** Base choices plus library ("lib:<id>") / uploaded ("upload:<id>") faces. */
export type EditFamily = "original" | "helv" | "times" | "cour" | (string & {});

/** An in-progress content-text edit (the overlay input's model). */
export interface ActiveTextEdit {
  /** Unique per edit — keys the overlay so React mounts a FRESH
   *  (uncontrolled) input; reusing one keeps the previous text. */
  uid: number;
  pageIndex: number;
  text: string;
  quads: Quad[];
  bbox: [number, number, number, number];
  origin: [number, number];
  size: number;
  base14: string;
  rawFont: string;
  color: string;
  /** Embedded font file for WYSIWYG editing via FontFace (if any). */
  fontBytes: Uint8Array | null;
  /** Present when the whole paragraph is being edited (Shift+click). */
  block?: { lineHeight: number; maxWidth: number; lineCount: number };
  // ── live restyling (formatting bar) ──
  family: EditFamily;
  bold: boolean;
  italic: boolean;
  /** As detected on the original line. Font changes (family/bold/italic)
   *  force the Base-14 variant; colour/size changes keep the original
   *  face but must still count as "something to commit". */
  origBold: boolean;
  origItalic: boolean;
  origColor: string;
  origSize: number;
  background: string | null;
  /** Live drag offset (view points) — commit erases the original and
   *  redraws at origin + (moveDx, moveDy), i.e. moving IS an edit. */
  moveDx: number;
  moveDy: number;
}

/** A style change aimed at the selected annotation(s). Deliberately a
 *  flat union of every styling key rather than Partial<Annotation>: one
 *  patch can land on several annotations of different kinds (all four
 *  markup kinds share a colour), so it is keyed by PROPERTY, not by
 *  which annotation shape happens to own it. */
export type AnnotStylePatch = Partial<{
  color: string;
  size: number;
  /** Text boxes: typeface id (see TextAnnotation.font). */
  font: string;
  /** Text boxes: which CJK ordering to draw Han with. */
  cjkScript: "zh-Hans" | "zh-Hant" | "ja" | "ko";
  /** Text boxes: weight and slant. */
  bold: boolean;
  italic: boolean;
  strokeWidth: number;
  fill: string | null;
  dash: DashStyle;
  headStart: HeadStyle;
  headEnd: HeadStyle;
  align: TextAlign;
  opacity: number;
}>;

export function isMarkupTool(t: Tool): t is MarkupKind {
  return t === "highlight" || t === "underline" || t === "strikeout" || t === "squiggly";
}

export function isShapeTool(t: Tool): t is ShapeKind {
  return t === "rect" || t === "ellipse" || t === "line" || t === "arrow";
}

/** Current tool styling forwarded to pages for live previews of the
 *  in-progress markup drag / ink stroke / shape drag. */
export interface ToolStyles {
  markupColor: string;
  inkColor: string;
  inkWidth: number;
  shapeColor: string;
  shapeWidth: number;
  shapeFill: string | null;
  shapeDash: DashStyle;
  shapeHeadStart: HeadStyle;
  shapeHeadEnd: HeadStyle;
}

/** "cancel" is a drag the system took away — a touch the browser
 *  reclaimed for scrolling, a lost pointer capture, an OS gesture. It
 *  means "put it back": drop the live preview and restore the state
 *  captured at "start" WITHOUT committing an undo step. Every handler
 *  that acts on "end" must also answer "cancel", or the interrupted
 *  drag leaves its draft on screen and its refs populated. */
export type DragPhase = "start" | "move" | "end" | "cancel";

/** One search match: the glyph quads of a single occurrence. */
export interface SearchHit {
  pageIndex: number;
  quads: Quad[];
}

export interface PendingStamp {
  dataUrl: string;
  /** Image pixel dims — the placement code scales them to points. */
  width: number;
  height: number;
}
